import React, { useMemo } from 'react'
import DOMPurify from 'dompurify'
import { MEDIA_BASE_URL } from './Gallery'
import { rewriteLegacyLink } from '../utils/legacyLinks'

/**
 * Renders migrated article HTML with its original structure intact
 * (headings, paragraphs, lists, tables, emphasis, links) rather than
 * flattening it to plain text.
 *
 * The content is sanitized client-side with DOMPurify using an allow-list that
 * matches the server-side sanitizer (backend/shared/validation.js): safe
 * formatting tags only, no scripts/styles/iframes/event handlers, and only
 * http(s)/mailto links. External links get target=_blank + rel=noopener.
 *
 * Migrated Joomla articles reference images with relative paths
 * ("images/flightlog/.../photo.jpg") that would otherwise resolve against the
 * page URL and 404; those are rewritten to the media CDN.
 */

const ALLOWED_TAGS = [
  'p', 'br', 'a', 'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'span', 'img', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
  'figure', 'figcaption', 'hr'
]
const ALLOWED_ATTR = ['href', 'target', 'rel', 'title', 'src', 'alt', 'colspan', 'rowspan']

/**
 * Rewrite a migrated relative image path to the media CDN. Leaves absolute
 * (http/https/data/protocol-relative) URLs untouched.
 */
export function resolveMediaSrc(src) {
  if (!src) return src
  if (/^(https?:)?\/\//i.test(src) || src.startsWith('data:')) return src
  // Normalise leading "./" and "/", then strip a leading "images/" since the
  // CDN is served under "/images/...".
  let path = src.replace(/^\.?\//, '').replace(/^images\//i, '')
  return `${MEDIA_BASE_URL}/images/${path}`
}

// Post-sanitize: force safe external links + fix relative image sources.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('href')) {
    let href = node.getAttribute('href')
    // Rewrite legacy Joomla internal links to our routes first.
    const rewritten = rewriteLegacyLink(href)
    if (rewritten) {
      node.setAttribute('href', rewritten)
      href = rewritten
      // Same-site link now: don't open in a new tab.
      node.removeAttribute('target')
      node.removeAttribute('rel')
    } else if (/^https?:\/\//i.test(href)) {
      // Genuinely external link.
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  }
  if (node.tagName === 'IMG' && node.getAttribute('src')) {
    node.setAttribute('src', resolveMediaSrc(node.getAttribute('src')))
    node.setAttribute('loading', 'lazy')
  }
})

function RichHtml({ html, className = '' }) {
  const clean = useMemo(() => {
    if (!html) return ''
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      // Drop the tag AND its content for these (don't leak inline scripts/styles as text)
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
      ALLOW_DATA_ATTR: false
    })
  }, [html])

  if (!clean) return null

  return (
    <div
      className={`rich-html ${className}`.trim()}
      // Content is sanitized by DOMPurify immediately above.
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  )
}

export default RichHtml
